import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ROUTES, ROUTE_CONFIG } from '@/constants/route-config';
import { extractLocaleFromPath } from '@/utils/locale';

interface RouteWithHelmetProps {
  children: React.ReactNode;
}

export function RouteWithHelmet({ children }: RouteWithHelmetProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const titles = getPageTitleByUrl(location.pathname, t);

  return (
    <HelmetProvider>
      <Helmet prioritizeSeoTags>
        <title>{titles.join(' | ')}</title>
      </Helmet>
      {children}
    </HelmetProvider>
  );
}

const getPageTitleByUrl = (
  url: Location['pathname'],
  t: TFunction,
): string[] => {
  const titleMap: { [key: string]: string } = {
    // Primary navigation
    [ROUTES.DASHBOARD]: t('page-title.dashboard', { defaultValue: 'Dashboard' }),
    [ROUTES.DISCOVER]: t('page-title.discover', { defaultValue: 'Discover' }),
    [ROUTES.BOT_SYSTEM]: t('page-title.bot-management', { defaultValue: 'Strategy Management' }),
    [ROUTES.BOT_DETAIL]: t('page-title.bot-details', { defaultValue: 'Strategy Details' }),
    [ROUTES.BOT_CREATE]: t('page-title.create-bot', { defaultValue: 'Create Strategy' }),
    [ROUTES.ALERT_HISTORY]: t('page-title.alert-history', { defaultValue: 'Alert History' }),
    [ROUTES.ALERT_DETAIL]: t('page-title.alert-detail', { defaultValue: 'Alert Detail' }),
    [ROUTES.STRATEGY_DETAIL]: t('page-title.strategy-detail', { defaultValue: 'Strategy Detail' }),
    [ROUTES.NETWORK]: t('page-title.network', { defaultValue: 'My Network' }),
    [ROUTES.USER_DETAIL]: t('page-title.user-detail', { defaultValue: 'User Profile' }),
    [ROUTES.CHANNELS]: t('page-title.channels', { defaultValue: 'Channels' }),
    [ROUTES.CHANNEL_DETAIL]: t('page-title.channel-detail', { defaultValue: 'Channel Details' }),
    [ROUTES.PRICING]: t('page-title.pricing', { defaultValue: 'Pricing' }),
    // Bot flow
    [ROUTES.BOT_FLOW_EDITOR]: t('page-title.bot-flow-editor', { defaultValue: 'Strategy Flow Editor' }),
    [ROUTES.BOT_FLOW_PREVIEW]: t('page-title.bot-flow-preview', { defaultValue: 'Strategy Flow Preview' }),
    // Account & billing
    [ROUTES.CREDIT_MANAGEMENT]: t('page-title.credits', { defaultValue: 'Credits' }),
    [ROUTES.PAYMENT_HISTORY]: t('page-title.payment-history', { defaultValue: 'Payment History' }),
    [ROUTES.SETTINGS]: t('page-title.settings', { defaultValue: 'Settings' }),
    // Payment flow
    [ROUTES.PAYMENT]: t('page-title.payment', { defaultValue: 'Payment' }),
    [ROUTES.PAYMENT_RESULT]: t('page-title.payment-result', { defaultValue: 'Payment Result' }),
  };

  const { path: pathWithoutLocale } = extractLocaleFromPath(url);

  const titleArr: string[] = [
    t('page-title.app-name', { defaultValue: 'Deview Monitoring' }),
  ];

  // Try exact path matches first
  if (titleMap[pathWithoutLocale]) {
    titleArr.unshift(titleMap[pathWithoutLocale]);
    return titleArr;
  }

  // Try to match path patterns (like /bot/:id)
  for (const routePath in titleMap) {
    // Convert route pattern to regex for matching
    // Replace :id or other parameters with regex pattern
    const pathPattern = routePath
      .replace(/\/:[^/]+/g, '/[^/]+')
      .replace(/\//g, '\\/');

    const pathRegex = new RegExp(`^${pathPattern}$`);

    if (pathRegex.test(pathWithoutLocale)) {
      titleArr.unshift(titleMap[routePath]);
      break;
    }
  }

  // Look for nested route titles in ROUTE_CONFIG
  const findRouteTitle = (path: string): void => {
    for (const route of ROUTE_CONFIG) {
      // Handle wildcard routes (like '*') separately to avoid regex errors
      const isWildcardRoute = route.path === '*';
      const isExactMatch = path === route.path;
      const isPatternMatch =
        !isWildcardRoute &&
        route.path.includes(':') &&
        path.match(new RegExp(`^${route.path.replace(/:[^/]+/g, '[^/]+')}$`));

      if (isExactMatch || isPatternMatch) {
        titleArr.unshift(
          t(`page-title.${route.path.replace(/^\//, '').replace(/\//g, '-')}`, {
            defaultValue: route.title,
          }),
        );
        return;
      }

      if (route.children) {
        for (const childRoute of route.children) {
          const fullChildPath = childRoute.path.startsWith('/')
            ? childRoute.path
            : `${route.path}/${childRoute.path}`.replace(/\/\//g, '/');
          if (
            path === fullChildPath ||
            path.match(
              new RegExp(`^${fullChildPath.replace(/:[^/]+/g, '[^/]+')}$`),
            )
          ) {
            titleArr.unshift(
              t(
                `page-title.${fullChildPath
                  .replace(/^\//, '')
                  .replace(/\//g, '-')}`,
                { defaultValue: childRoute.title },
              ),
            );
            // Add parent title for better context
            titleArr.unshift(
              t(
                `page-title.${route.path
                  .replace(/^\//, '')
                  .replace(/\//g, '-')}`,
                { defaultValue: route.title },
              ),
            );
            return;
          }
        }
      }
    }
  };

  findRouteTitle(pathWithoutLocale);

  return titleArr;
};

export default RouteWithHelmet;
